import React, { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useFetchWidget } from 'services/widget'
import { BarChart } from 'components/BarChart'
import { ColumnChart } from 'components/ColumnChart'
import { DonutChart } from 'components/DonutChart'
import { PyramidChart } from 'components/PyramidChart'
import { Widget as WidgetModel } from 'models/widget'
import { Heading } from 'components/Heading'
import { Card } from 'components/Card'
import { Formula } from 'components/Formula'
import { Stack } from 'components/Stack'
import { Spinner } from 'components/Spinner'
import styles from './Widget.module.scss'
import { Flex } from 'components/Flex'
import { useTranslation } from 'react-i18next'
import { Text } from 'components/Text'
import { Icon } from 'components/Icon'
import { sum } from 'ramda'
import { useProjectContext } from 'context/project-context'
type WidgetProps = {
  widget: WidgetModel
  layerId: number
}
const AVAILABLE_WIDGETS = {
  BarChart,
  ColumnChart,
  DonutChart,
  PyramidChart,
  Formula,
}
export const Widget: FC<WidgetProps> = ({ widget, layerId }) => {
  const { t } = useTranslation()
  const { projectId, areaId } = useParams()
  const [{ boundingBox, filters, hiddenLayers }, projectDispatch] = useProjectContext()
  const statusclipped = localStorage.getItem('clipped')

  const widgetFilters = {
    widgetId: widget.id,
    projectId,
    areasIds: areaId,
    ...(areaId ? null : boundingBox),
    filters: filters,
  }

  const widgetProjectFilters = {
    widgetId: widget.id,
    projectId,
    areasIds: areaId,
    ...(areaId ? null : boundingBox),
    filters: filters,
  }

  const { data, status } = useFetchWidget(
    statusclipped === 'false' ? widgetFilters : widgetProjectFilters,
  )
  const Component = AVAILABLE_WIDGETS[widget.type]
  const selectedFilter = filters[widget.sourceid]?.[widget.column]?.filter

  const onFilterChanged = (filter: any) => {
    projectDispatch.setFilters({
      sourceid: widget.sourceid,
      column: widget.column,
      operator: widget.operator,
      filter: filter,
    })
  }
  const isLayerHidden = hiddenLayers ? hiddenLayers.includes(layerId) : false
  const handleLayerDisplay = () => {
    projectDispatch.changeLayerVisibility(layerId, isLayerHidden)
  }
  const isFormula = widget.type === 'Formula'
  const hasTotal = !isFormula && widget.type !== 'PyramidChart'
  //const isEmpty = !isFormula && sum(data?.values?.flat() || []) === 0
  return (
    <Card>
      <Stack space="medium">
        <Stack space="small">
          <Flex justify="between">
            <Heading size="widget" className={styles.title}>
              {widget.title}
            </Heading>
            <button
              onClick={handleLayerDisplay}
              className={styles.widgetButton}
              aria-label={isLayerHidden ? t('widgets.show') : t('widgets.hide')}
            >
              {isLayerHidden ? (
                <Flex align="center" gap="xs">
                  <Text size="small">{t('widgets.show')}</Text> <Icon name="eye-off-line" />
                </Flex>
              ) : (
                <Flex align="center" gap="xs">
                  <Text size="small">{t('widgets.hide')}</Text>
                  <Icon name="eye-line" />
                </Flex>
              )}
            </button>
          </Flex>
          {hasTotal ? (
            <Flex justify="between">
              <Text>
                <Text component="span" weight="strong" className={styles.totalTitle}>
                  {t('widgets.total')}:
                </Text>{' '}
                {data?.values?.map((values: number[], index: number) => {
                  return (
                    <span key={index}>
                      <Text component="span" weight="strong" className={styles.totalNumber}>
                        {sum(values)}
                      </Text>
                      {data.values.length > 1 && index < data.values.length - 1 && (
                        <Text component="span"> {t('widgets.vs')} </Text>
                      )}
                    </span>
                  )
                })}
              </Text>
              {selectedFilter?.length > 0 && (
                <button className={styles.widgetButton} onClick={() => onFilterChanged([])}>
                  <Flex align="center" gap="xs">
                    <Text size="small" align="right">
                      {t('widgets.single_clear_filter')}
                    </Text>
                    <Icon name="restart-line" />
                  </Flex>
                </button>
              )}
            </Flex>
          ) : null}
        </Stack>
        {status === 'loading' ? (
          <Flex justify="center" align="center" className={styles.spinner}>
            <Spinner />
          </Flex>
        ) : (
          <div
            style={{ overflowY: 'auto', overflow: 'auto' }}
            className={isFormula ? '' : styles.widgetContainer}
          >
            <Component
              data={data as any}
              enableTooltips
              onSelect={onFilterChanged}
              selected={selectedFilter}
            />
          </div>
        )}
      </Stack>
    </Card>
  )
}
Widget.displayName = 'Widget'
